import { useEffect, useState } from "react";
import { CircleMarker, Polygon, Polyline, Tooltip, useMap } from "react-leaflet";

const R = 6378137;
const rad = (d) => (d * Math.PI) / 180;

export function geodesicArea(latlngs) {
  let area = 0;
  const n = latlngs.length;
  if (n < 3) return 0;
  for (let i = 0; i < n; i++) {
    const p1 = latlngs[i], p2 = latlngs[(i + 1) % n];
    area += rad(p2[1] - p1[1]) * (2 + Math.sin(rad(p1[0])) + Math.sin(rad(p2[0])));
  }
  return Math.abs((area * R * R) / 2);
}

export const fmtDistance = (m) => (m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${m.toFixed(1)} m`);
export const fmtArea = (m2) => (m2 >= 10000 ? `${(m2 / 10000).toFixed(3)} ha (${(m2 / 4046.86).toFixed(2)} acres)` : `${m2.toFixed(1)} m²`);

export function measureSummary(mode, pts, map) {
  if (pts.length < 2) return null;
  let dist = 0;
  for (let i = 1; i < pts.length; i++) dist += map.distance(pts[i - 1], pts[i]);
  if (mode === "distance") return { primary: fmtDistance(dist), secondary: `${pts.length} points` };
  if (pts.length < 3) return { primary: fmtDistance(dist), secondary: "add a third point for area" };
  const perimeter = dist + map.distance(pts[pts.length - 1], pts[0]);
  return { primary: fmtArea(geodesicArea(pts)), secondary: `perimeter ${fmtDistance(perimeter)}` };
}

export function MeasureLayer({ mode, points, onPoints, onSummary, locked = false, onFinish }) {
  const map = useMap();
  const [cursor, setCursor] = useState(null);

  useEffect(() => {
    if (!mode) { setCursor(null); return undefined; }
    const container = map.getContainer();
    container.style.cursor = locked ? "" : "crosshair";
    map.doubleClickZoom.disable();
    const minPts = mode === "area" ? 3 : 2;
    const onClick = (e) => {
      if (locked) return;
      const last = points[points.length - 1];
      // Touch flow: tapping the last point again finishes the shape.
      if (last && points.length >= minPts && map.latLngToContainerPoint(e.latlng).distanceTo(map.latLngToContainerPoint(last)) < 14) { setCursor(null); onFinish?.(); return; }
      onPoints((p) => [...p, [e.latlng.lat, e.latlng.lng]]);
    };
    const onMove = (e) => { if (!locked) setCursor([e.latlng.lat, e.latlng.lng]); };
    const finish = (e) => { if (e?.originalEvent) { e.originalEvent.preventDefault?.(); } setCursor(null); if (points.length >= minPts) onFinish?.(); };
    map.on("click", onClick); map.on("mousemove", onMove); map.on("dblclick", finish); map.on("contextmenu", finish);
    return () => { map.off("click", onClick); map.off("mousemove", onMove); map.off("dblclick", finish); map.off("contextmenu", finish); container.style.cursor = ""; map.doubleClickZoom.enable(); };
  }, [mode, map, onPoints, locked, points, onFinish]);

  useEffect(() => { onSummary(measureSummary(mode, points, map)); }, [mode, points, map, onSummary]);

  if (!mode || points.length === 0) return null;
  const preview = cursor && !locked ? [...points, cursor] : points;
  const style = { color: "#C85A32", weight: 3, dashArray: locked ? null : "6 6", fillColor: "#C85A32", fillOpacity: locked ? 0.25 : 0.15 };
  const summary = measureSummary(mode, points, map);
  return (
    <>
      {mode === "area" && preview.length >= 3 ? <Polygon positions={preview} pathOptions={style} /> : <Polyline positions={preview} pathOptions={style} />}
      {points.map((p, i) => (
        <CircleMarker key={i} center={p} radius={5} pathOptions={{ color: "#fff", weight: 2, fillColor: "#C85A32", fillOpacity: 1 }}>
          {i === points.length - 1 && summary && <Tooltip permanent direction="top" offset={[0, -8]} className="measure-tip">{summary.primary}</Tooltip>}
        </CircleMarker>
      ))}
    </>
  );
}
